/**
 * The AGG-checked feedback paragraph in the rejection email — whether it is available, whether it
 * is currently IN the body, and the one click that puts it in or takes it out.
 *
 * Lives beside ArchiveBody rather than inside it: the body is a contentEditable field, so this is
 * the one piece of that component that reaches into the DOM on its own schedule.
 */
import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { EmailDraft } from '../../types/taActionBoard';
import {
  hasFeedbackBlock,
  insertFeedbackBlock,
  removeFeedbackBlock,
} from './aggFeedbackBlock';
import { contentFor } from './emailContentRules';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';

export function useAggFeedbackParagraph(
  draft: EmailDraft | null,
  bodyRef: RefObject<HTMLDivElement | null>
) {
  /**
   * The AGG-filtered prose the Log page keeps per decision — IN the email by default (since
   * Sep 18 2026), out again on one click. The Tech-case template already writes it into the body,
   * so there the button is unavailable rather than adding a second copy. Added on open and the
   * recruiter decides whether to take it OUT; every send goes past their eyes.
   */
  const feedbackBlock = draft?.feedbackBlockHtml ?? '';
  const feedbackAvailable = Boolean(feedbackBlock) && !draft?.usesWrittenFeedback && !draft?.emailError;
  const [feedbackAdded, setFeedbackAdded] = useState(feedbackAvailable);
  // A switch can make the paragraph available after open; it then goes in by default too. A removal
  // while it stays available is kept: this reacts only when availability itself changes.
  const [wasAvailable, setWasAvailable] = useState(feedbackAvailable);
  if (wasAvailable !== feedbackAvailable) {
    setWasAvailable(feedbackAvailable);
    setFeedbackAdded(feedbackAvailable);
  }

  /**
   * Does adding the feedback REPLACE the "feel free to reach out" paragraph? Only on the
   * feedback-on-request variant: an email that now carries those insights should not also invite the
   * candidate to ask for them (Anna Thiel, 16 Sep 2026). The feedback-CALL variant keeps its
   * paragraph — a 15-minute call is a channel the written feedback does not replace, and that
   * paragraph holds the only link to it. `contentFor` is the same rule the warnings below read.
   */
  const replacesOffer = draft
    ? contentFor(draft.branch.split('_')[0], draft.branchKey) === 'on-request'
    : false;

  function toggleFeedback() {
    const el = bodyRef.current;
    if (!el) return;
    if (hasFeedbackBlock(el)) {
      removeFeedbackBlock(el);
      setFeedbackAdded(false);
      void logAnalyticsEvent('ta_agg_feedback_toggled', { added: false, stage: draft?.branchKey || 'unknown' });
      return;
    }
    if (insertFeedbackBlock(el, feedbackBlock, { replacesOffer })) {
      setFeedbackAdded(true);
      void logAnalyticsEvent('ta_agg_feedback_toggled', { added: true, stage: draft?.branchKey || 'unknown' });
    }
  }

  // Re-insert the AGG paragraph whenever it should be present and isn't: on open, and after a
  // language/template switch (which replaces the whole body) — in the new language, since the draft
  // carries a redrafted block. Runs after EmailBodyEditor's own write, so it sees the rendered body.
  useEffect(() => {
    const el = bodyRef.current;
    if (!feedbackAdded || !el || !feedbackBlock || hasFeedbackBlock(el)) return;
    insertFeedbackBlock(el, feedbackBlock, { replacesOffer });
  }, [bodyRef, draft, feedbackAdded, feedbackBlock, replacesOffer]);

  return { feedbackAvailable, feedbackAdded, replacesOffer, toggleFeedback };
}
